/** RaiderMapCR - Review verification page */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';

const VerifyReviewPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyReview(token);
    } else {
      setError('No verification token provided');
      setLoading(false);
    }
  }, [searchParams]);

  const verifyReview = async (token) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/reviews?token=${token}`);
      const data = await response.json();

      if (response.ok) {
        setResult(data);
      } else {
        setError(data.error || 'Verification failed');
      }
    } catch (err) {
      setError('Network error occurred during verification');
    } finally {
      setLoading(false);
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoToDirectory = () => {
    navigate('/directory');
  };

  if (loading) {
    return (
      <div className="main-content">
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#AA3333', mb: 3 }} />
            <Typography variant="h5" sx={{ color: '#ffffff' }}>
              Verifying your review...
            </Typography>
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div className="main-content">
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card sx={{
          backgroundColor: 'rgba(42, 42, 42, 0.75)',
          border: '1px solid rgba(74, 74, 74, 0.6)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            {error ? (
              <>
                <ErrorIcon sx={{ fontSize: 64, color: '#f44336', mb: 2 }} />
                <Typography variant="h5" sx={{ color: '#ffffff', mb: 2 }}>
                  Verification Failed
                </Typography>
                <Alert severity="error" sx={{ mb: 3, backgroundColor: 'rgba(211, 47, 47, 0.1)' }}>
                  {error}
                </Alert>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
                  The verification token may be invalid or expired.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={handleGoHome}
                    sx={{
                      borderColor: 'rgba(74, 74, 74, 0.6)',
                      color: '#ffffff',
                      '&:hover': {
                        borderColor: '#AA3333',
                        backgroundColor: 'rgba(170, 51, 51, 0.1)'
                      }
                    }}
                  >
                    Go Home
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleGoToDirectory}
                    sx={{
                      backgroundColor: '#AA3333',
                      '&:hover': {
                        backgroundColor: '#8B2A2A',
                      },
                    }}
                  >
                    View Directory
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <CheckCircleIcon sx={{ fontSize: 64, color: '#4CAF50', mb: 2 }} />
                <Typography variant="h5" sx={{ color: '#ffffff', mb: 2 }}>
                  Review Verified!
                </Typography>
                <Alert severity="success" sx={{ mb: 3, backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
                  {result?.message || 'Your review has been verified and published successfully!'}
                </Alert>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
                  Thank you for sharing your experience. Your review is now visible to other students.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={handleGoHome}
                    sx={{
                      borderColor: 'rgba(74, 74, 74, 0.6)',
                      color: '#ffffff',
                      '&:hover': {
                        borderColor: '#AA3333',
                        backgroundColor: 'rgba(170, 51, 51, 0.1)'
                      }
                    }}
                  >
                    Go Home
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleGoToDirectory}
                    sx={{
                      backgroundColor: '#AA3333',
                      '&:hover': {
                        backgroundColor: '#8B2A2A',
                      },
                    }}
                  >
                    View Directory
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </div>
  );
};

export default VerifyReviewPage;
